/**
 * The workshop film: thirty seconds from a kitchen table under a cloud of sawdust to a
 * workshop in the yard, by way of the designer, the plans, the lumber yard and the build
 * guide — on a slab, in 2x6, with windows down the bench wall, as the 12×16 workshop in
 * the catalog is.
 *
 * Eight scenes on one clock. The build follows the guide's own steps for a shed on a slab
 * in the guide's own order and under its own titles — the slab formed and poured, the
 * sills bolted down, the walls framed flat with their openings and stood up, the cap
 * plates, the rafters, the siding, the roof, the door. Nothing in it is a figure from a
 * real plan: the designer's numbers are the shopper's own choices, and the price is the
 * site's (`purchase/price.ts`).
 */

import { BUY, button, designScene, knob, option, type Control, type DesignScript } from "./designer.js";
import { drawDog } from "./dog.js";
import { driveScene, shopScene, type Shopping } from "./errands.js";
import type { Beat, Film, Frame, Site, View } from "./film.js";
import type { Ink, Pt } from "./ink.js";
import {
  GROUND,
  bird,
  caption,
  dust,
  hammerInHand,
  impactStar,
  ladder,
  note,
  shoot,
  sparkle,
  thought,
  titleBlock,
  wipe,
  type Camera,
} from "./props.js";
import {
  CROUCH,
  KNEEL,
  STAND,
  THUMBS_UP,
  blend,
  drawFigure,
  hammer,
  hammerStanding,
  idle,
  impact,
  walk,
  type Figure,
  type Pose,
} from "./rig.js";
import {
  BUILT,
  NOTHING,
  SLAB,
  SLAB_WORK,
  drawShed,
  floorOf,
  frontTop,
  project,
  quickBuild,
  roofHeight,
  type Progress,
  type Projection,
  type Shape,
} from "./shed.js";
import { clamp, cueAt, ease, mix, pulse, span } from "./timing.js";
import {
  birdhouse,
  dogBed,
  dustCloud,
  dusted,
  fruitBowl,
  handSaw,
  kitchen,
  mat,
  pawPrint,
  pegboard,
  pourStream,
  sawdust,
  screed,
  shopWindow,
  stroke,
  table,
  wheelbarrow,
  workbench,
  wrenchInHand,
} from "./workshopProps.js";
import { blowsOf, guideCards, shadow, yard, type Day, type Hammering, type Step } from "./yard.js";

/** The workshop: twelve by sixteen on a slab, eight-foot walls, two windows down one side. */
export const WORKSHOP: Shape = {
  width: 12,
  depth: 16,
  wall: 8,
  rise: 3,
  ridge: 0.5,
  doubleDoor: false,
  windows: 2,
  foundation: "slab",
};

/** When each scene starts. */
export const AT = {
  kitchen: 0,
  design: 4.6,
  shop: 10.4,
  drive: 13.6,
  build: 15.2,
  inside: 24.4,
  outside: 26.0,
  card: 27.4,
  end: 30,
};

function beats(site: Site): readonly Beat[] {
  return [
    {
      at: AT.kitchen,
      caption: "Need room to make things?",
      action: "A maker saws a board across the kitchen table. The offcut drops, the sawdust billows, and the dog sneezes itself off its bed.",
    },
    { at: 2.4, action: "Dusted from cap to boots, the maker pictures a workshop." },
    {
      at: AT.design,
      caption: "Design it for the work.",
      action:
        "In the shed designer, the maker sizes it up, puts it on a slab, frames the walls in 2x6 and adds two windows " +
        "along one wall.",
    },
    {
      at: 8.65,
      caption: `Get the plans — ${site.price}`,
      action: "One click on Buy plans, and the framing drawings, the cut list and the build guide come off the printer.",
    },
    {
      at: AT.shop,
      caption: "Shop the list.",
      action:
        "At the lumber yard the cart fills a line at a time: concrete, treated sills, 2x6 studs, rafters, plywood, siding " +
        "and shingles.",
    },
    { at: AT.drive, action: "Home again, with the lumber on the roof and the dog at the window." },
    {
      at: AT.build,
      caption: "Build it, step by step.",
      action:
        "The workshop goes up in the build guide's order: the slab formed and poured, the sills bolted down — and the dog " +
        "across the fresh concrete — the walls framed flat with their openings and stood up, the cap plates, the rafters, " +
        "the siding, the roof and the door.",
    },
    { at: AT.inside, action: "Inside, at the bench under the windows, the maker finishes a birdhouse while the dog sleeps on its mat." },
    { at: AT.outside, caption: "Your shop. Your size.", action: "The birdhouse goes up in the tree, and a bird moves in." },
    { at: AT.card, action: `${site.brand}: build plans drawn to your dimensions, ${site.price}.` },
  ];
}

export const workshopFilm: Film = {
  duration: AT.end,
  title: "From plan to workshop in thirty seconds",
  slug: "workshop",
  summary:
    "A drawn film: a maker with no room to make things designs a workshop, buys the plans, shops the list and " +
    "builds it on a slab, step by step.",
  lead: (site) =>
    `No room to make things, a workshop designed for the work, the plans for ${site.price}, a trip for exactly ` +
    "what is on the list, and the build on a slab — in the order the plan's own build guide puts it.",
  beats,
  draw,
};

function draw(frame: Frame): void {
  const { t, ink, view, site } = frame;
  if (t < AT.design) {
    kitchenScene(ink, view, t - AT.kitchen);
  } else if (t < AT.shop) {
    designScene(ink, view, t - AT.design, site, DESIGNING);
  } else if (t < AT.drive) {
    shopScene(ink, view, t - AT.shop, SHOPPING);
  } else if (t < AT.build) {
    driveScene(ink, view, t - AT.drive);
  } else if (t < AT.inside || t >= AT.outside) {
    buildScene(ink, view, t - AT.build, site);
  } else {
    insideScene(ink, view, t - AT.inside);
  }

  for (const line of captions(site)) {
    caption(ink, line.text, t, line.start, line.end, { accent: line.accent, size: line.size });
  }
  for (const at of WIPES) {
    wipe(ink, view, t, at);
  }
}

/** The film's own words on screen, and when: in the film's seconds. */
export function captions(site: Site): { text: string; start: number; end: number; accent?: string; size?: number }[] {
  return [
    { text: "Need room to make things?", start: 1.45, end: 3.85, size: 54 },
    { text: "Design it for the work.", start: 4.95, end: 8.5 },
    { text: `Get the plans — ${site.price}`, start: 8.68, end: 10.05, accent: site.price },
    { text: "Shop the list.", start: 10.7, end: 13.25 },
    { text: "Build it, step by step.", start: 15.5, end: 17.9 },
    { text: "Your shop. Your size.", start: 26.15, end: 27.3 },
  ];
}

/** Where a sheet of paper is pulled across the frame between scenes. */
export const WIPES: readonly number[] = [AT.design, AT.shop, AT.drive, AT.build, AT.inside, AT.outside];

// ---- 1. the kitchen ---------------------------------------------------------------------

/** The kitchen table, and the board across it with its end over the edge. */
const TABLE = { x0: -80, x1: 170, top: 176 };
const BOARD = { x0: -176, x1: 128, y: 164 };
/** Where the saw goes through the board. */
const CUT = -118;
const BED = 230;
const BUBBLE = { x: 60, y: -30 };

/** The kitchen scene's moments, in its own seconds: the picture and the score both read them. */
export const KITCHEN = {
  sawing: [0.15, 1.3] as const,
  /** Strokes of the saw a second. */
  rate: 3.2,
  /** The offcut, from the last stroke to the floor. */
  drop: [1.3, 1.62] as const,
  billow: [1.3, 2.0] as const,
  /** As the cloud's edge reaches the dog's nose (`CLOUD`), and the dog is in plain view. */
  sneeze: [1.9, 2.3] as const,
  settle: [2.0, 2.8] as const,
  think: [2.4, 3.3] as const,
  /** The workshop in the thought bubble, built start to finish. */
  vision: [2.85, 3.6] as const,
  happy: 3.3,
  zoom: [3.85, 4.6] as const,
};

/**
 * The sawdust cloud: where it billows from, and how far it reaches — over the maker, the
 * table and the window, and stopping at the dog's nose, so the sneeze is seen, not heard.
 */
const CLOUD = { x: CUT + 40, y: 120, from: 30, reach: 270 };

/** The saw's line through the board: the blade's angle, and the hand's path along it. */
const SAW = { angle: 0.95, hand: { x: -160, y: 116 }, reach: 13 };

function kitchenScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  const zoom = ease.in(span(u, ...KITCHEN.zoom));
  const camera: Camera = { x: mix(36, BUBBLE.x, zoom), y: mix(118, BUBBLE.y, zoom), zoom: mix(1.55, 5.5, zoom) };
  shoot(
    ink,
    camera,
    (v) => {
      kitchen(ink, v, GROUND);
      dogBed(ink, BED, GROUND);
      table(ink, TABLE.x0, TABLE.x1, TABLE.top, GROUND);
      fruitBowl(ink, 120, BOARD.y, span(u, KITCHEN.drop[1], KITCHEN.drop[1] + 0.3));

      // The board, and its end until the saw is through it.
      ink.rect(CUT, BOARD.y, BOARD.x1 - CUT, 12, { fill: p.wood, width: 2.4 });
      const drop = span(u, ...KITCHEN.drop);
      const { ctx } = ink;
      ctx.save();
      ctx.translate(mix(BOARD.x0, BOARD.x0 - 16, drop), mix(BOARD.y, GROUND - 12, ease.bounce(drop)));
      ctx.rotate(mix(0, -0.12, ease.out(drop)));
      ink.rect(0, 0, CUT - BOARD.x0, 12, { fill: p.wood, width: 2.4 });
      ctx.restore();
      if (drop >= 1) {
        dust(ink, BOARD.x0 + 10, GROUND, span(u, KITCHEN.drop[1], KITCHEN.drop[1] + 0.4), 1.2);
      }
      sawdust(ink, CUT, BOARD.y + 12, GROUND, u, ...KITCHEN.sawing);

      // The dog: asleep on its bed, sneezed off it by the dust, sitting up offended.
      const sneeze = span(u, ...KITCHEN.sneeze);
      if (sneeze <= 0) {
        drawDog(ink, { x: BED, ground: GROUND - 10, facing: -1, mode: "sleep", t: u });
      } else {
        drawDog(ink, {
          x: BED + 32 * ease.out(sneeze),
          ground: sneeze < 1 ? GROUND - 10 * (1 - sneeze) : GROUND,
          facing: -1,
          mode: sneeze < 1 ? "jump" : "sit",
          air: sneeze,
          t: u,
        });
        dust(ink, BED - 34, GROUND - 40, span(u, KITCHEN.sneeze[0], KITCHEN.sneeze[0] + 0.35), 0.8);
      }

      // The maker: sawing, frozen in the cloud, then dusted and dreaming.
      const s = stroke(u, ...KITCHEN.sawing, KITCHEN.rate);
      const sawing = u < KITCHEN.sawing[1];
      const along = { x: Math.cos(SAW.angle), y: Math.sin(SAW.angle) };
      const hand = sawing
        ? { x: SAW.hand.x + along.x * SAW.reach * s, y: SAW.hand.y + along.y * SAW.reach * s }
        : { x: SAW.hand.x - 8, y: SAW.hand.y - 26 };
      const dreaming = u > KITCHEN.think[0];
      const happy = u > KITCHEN.happy;
      const pose: Pose = { ...STAND, lean: sawing ? 0.22 : 0.02, nod: dreaming ? -0.25 : 0.05 };
      const figure: Figure = {
        x: -200,
        ground: GROUND,
        facing: 1,
        reachA: hand,
        reachB: sawing ? { x: -150, y: BOARD.y - 2 } : { x: -176, y: 150 },
        eyes: happy ? "happy" : u > KITCHEN.billow[0] && u < KITCHEN.settle[1] ? "wide" : u > KITCHEN.settle[1] ? "open" : "open",
        mouth: happy ? "grin" : u > KITCHEN.billow[0] ? "o" : "flat",
        look: dreaming ? 0.4 : 0.6,
        capTilt: u > KITCHEN.billow[0] ? 0.25 : 0,
      };
      const at = drawFigure(ink, pose, figure);
      handSaw(ink, at.handA, sawing ? SAW.angle : SAW.angle + 0.9);
      dusted(ink, [at.head, at.shoulder, at.handA, at.pelvis], span(u, KITCHEN.billow[1] - 0.1, KITCHEN.billow[1] + 0.3));

      // The cloud: swelling over the maker and the table, then thinning to a haze.
      const billow = span(u, ...KITCHEN.billow);
      dustCloud(ink, CLOUD.x, CLOUD.y, billow > 0 ? mix(CLOUD.from, CLOUD.reach, ease.inOut(billow)) : 0, span(u, ...KITCHEN.settle));

      // The workshop it dreams of.
      thought(ink, { x: at.head.x + 20, y: at.head.y - 20 }, BUBBLE, span(u, ...KITCHEN.think), 64);
      const build = span(u, ...KITCHEN.vision);
      if (build > 0) {
        drawShed(ink, { x: BUBBLE.x - 46, y: BUBBLE.y + 44, scale: 3.3 }, WORKSHOP, quickBuild(build));
      }
    },
    view,
  );
}

// ---- 2. designing it, and buying the plans ------------------------------------------------

/** The design scene's moments, in its own seconds. */
export const DESIGN = {
  widthDrag: [0.55, 1.0] as const,
  lengthDrag: [1.3, 1.8] as const,
  slabClick: 2.2,
  studClick: 2.6,
  windowClicks: [3.0, 3.35] as const,
  buyClick: 4.05,
  delighted: 4.3,
  pullBack: [4.4, 5.0] as const,
  printing: [4.6, 5.5] as const,
  pages: { out: 4.65, step: 0.2, fly: [0.2, 0.55] as const },
  stand: [4.85, 5.2] as const,
  hop: [5.3, 5.6] as const,
};

/** What the designer shows at `u`: from the storage shed's ten by twelve to the workshop. */
export function designState(u: number): {
  width: number;
  length: number;
  slab: boolean;
  twoBySix: boolean;
  windows: number;
  windowPress: number;
  press: number;
  paid: number;
} {
  const [first, second] = DESIGN.windowClicks;
  return {
    width: mix(10, 12, ease.inOut(span(u, ...DESIGN.widthDrag))),
    length: mix(12, 16, ease.inOut(span(u, ...DESIGN.lengthDrag))),
    slab: u >= DESIGN.slabClick,
    twoBySix: u >= DESIGN.studClick,
    windows: u >= second ? 2 : u >= first ? 1 : 0,
    windowPress: Math.max(pulse(u, first, first + 0.2), pulse(u, second, second + 0.2)),
    press: pulse(u, DESIGN.buyClick, DESIGN.buyClick + 0.2),
    paid: span(u, DESIGN.buyClick + 0.2, DESIGN.buyClick + 0.3),
  };
}

/** The panel, in the configurator's own words (`ui/fields.ts`). */
function controls(state: ReturnType<typeof designState>): Control[] {
  return [
    { kind: "slider", label: "Width", value: state.width, min: 4, max: 24 },
    { kind: "slider", label: "Length", value: state.length, min: 4, max: 24 },
    { kind: "choice", label: "Foundation", options: ["Skids", "Piers", "Slab"], chosen: state.slab ? 2 : 0 },
    { kind: "choice", label: "Wall framing", options: ["2x4", "2x6"], chosen: state.twoBySix ? 1 : 0 },
    { kind: "buttons", label: "Doors & windows", buttons: ["+ Door", "+ Window"], pressed: 1, press: state.windowPress },
  ];
}

const PANEL = controls(designState(0));

/** Sized up from ten by twelve, put on a slab, framed in 2x6, two windows, and bought. */
export const DESIGNING: DesignScript = {
  at(u) {
    const state = designState(u);
    const { width, length } = state;
    return {
      controls: controls(state),
      width,
      length,
      shape: { ...WORKSHOP, width, depth: length, foundation: state.slab ? "slab" : "runners", windows: state.windows },
      press: state.press,
      paid: state.paid,
    };
  },
  keys: [
    { at: 0, x: 150, y: 175 },
    { at: 0.5, ...knob(PANEL, 0, 10) },
    { at: DESIGN.widthDrag[0], ...knob(PANEL, 0, 10) },
    { at: DESIGN.widthDrag[1], ...knob(PANEL, 0, 12) },
    { at: 1.25, ...knob(PANEL, 1, 12) },
    { at: DESIGN.lengthDrag[0], ...knob(PANEL, 1, 12) },
    { at: DESIGN.lengthDrag[1], ...knob(PANEL, 1, 16) },
    { at: DESIGN.slabClick - 0.05, ...option(PANEL, 2, 2) },
    { at: DESIGN.studClick - 0.05, ...option(PANEL, 3, 1) },
    { at: DESIGN.windowClicks[0] - 0.05, ...button(PANEL, 4, 1) },
    { at: DESIGN.windowClicks[1], ...button(PANEL, 4, 1) },
    { at: DESIGN.buyClick - 0.08, x: BUY.x + 6, y: BUY.y + 1 },
    { at: 5.8, x: BUY.x + 6, y: BUY.y + 1 },
  ],
  clicks: [DESIGN.widthDrag[0], DESIGN.lengthDrag[0], DESIGN.slabClick, DESIGN.studClick, ...DESIGN.windowClicks, DESIGN.buyClick],
  timing: DESIGN,
};

// ---- 3. the lumber yard, and 4. the drive home ----------------------------------------------

/** The list, a line at a time onto the cart, in the shop scene's seconds. */
export const SHOPPING: Shopping = {
  list: [
    { kind: "concrete", height: 22, line: "Concrete", at: 0.35 },
    { kind: "treated", height: 21, line: "Treated sills", at: 0.65 },
    { kind: "studs", height: 21, line: "2x6 studs", at: 0.95 },
    { kind: "joists", height: 30, line: "Rafters", at: 1.25 },
    { kind: "plywood", height: 14, line: "Plywood", at: 1.55 },
    { kind: "siding", height: 12, line: "Siding", at: 1.85 },
    { kind: "shingles", height: 18, line: "Shingles", at: 2.15 },
  ],
  aisle: [0, 3.05],
};

// ---- 5. building it, and 7. done ------------------------------------------------------------

const SITE: Projection = { x: -110, y: GROUND, scale: 22 };

/** The build's day: across in nine and a half seconds, gold from five. */
const DAY: Day = { length: 9.5, evening: [5, 11], tree: -390 };

/** The build, on the scene's own clock: each stage's start and end, in the guide's order. */
export const PLAN = {
  slab: [0.25, 2.0],
  sills: [2.0, 2.6],
  left: [2.6, 2.9, 3.15],
  back: [3.15, 3.45, 3.7],
  right: [3.7, 4.2, 4.5],
  front: [4.5, 4.9, 5.4],
  caps: [5.4, 5.75],
  rafters: [5.75, 6.95],
  siding: [6.95, 7.95],
  trim: [7.8, 8.2],
  roofDeck: [7.95, 8.4],
  shingles: [8.35, 8.9],
  doors: [8.75, 9.2],
} as const;

/** Where the slab's stage turns from forms to pouring, and from pouring to striking off. */
const slabAt = (k: number): number => PLAN.slab[0] + k * (PLAN.slab[1] - PLAN.slab[0]);
export const POUR = [slabAt(SLAB_WORK.set), slabAt(SLAB_WORK.poured)] as const;

/**
 * The guide's steps for a shed on a slab, under the titles the plan prints them with, as
 * the card shows them. `test/film.test.ts` finds every one of them in `guide.cpp`.
 */
export const STEPS: readonly Step[] = [
  { at: 0.25, title: "Form and pour the slab" },
  { at: 2.0, title: "Bolt down the sills" },
  { at: 2.6, title: "Frame the walls flat" },
  { at: 3.7, title: "Frame the openings" },
  { at: 4.9, title: "Stand the walls and brace them" },
  { at: 5.4, title: "Lap the cap plates over the corners" },
  { at: 5.75, title: "Cut and hang the rafters" },
  { at: 6.95, title: "Sheathe the walls" },
  { at: 7.95, title: "Deck the roof and cover it" },
  { at: 8.75, title: "Hang the door" },
];

/** How far the build is at `u` seconds into the scene. */
export function buildProgress(u: number): Progress {
  const s = (stage: readonly [number, number]): number => span(u, stage[0], stage[1]);
  const wall = (w: readonly [number, number, number]): { frame: number; raise: number } => ({
    frame: span(u, w[0], w[1]),
    raise: span(u, w[1], w[2]),
  });
  return {
    ...NOTHING,
    slab: s(PLAN.slab),
    sills: s(PLAN.sills),
    left: wall(PLAN.left),
    back: wall(PLAN.back),
    right: wall(PLAN.right),
    front: wall(PLAN.front),
    caps: s(PLAN.caps),
    rafters: s(PLAN.rafters),
    siding: s(PLAN.siding),
    trim: s(PLAN.trim),
    roofDeck: s(PLAN.roofDeck),
    shingles: s(PLAN.shingles),
    doors: s(PLAN.doors),
  };
}

/** Where the maker works through the build: a spot per stretch of it, popped between. */
export const SPOTS = [0, POUR[0], POUR[1], 2.0, 2.6, 4.5, 5.4, 6.95, 7.95, 8.75, 9.2];

/** How fast the maker hammers at each spot (`yard.ts`, `Hammering`). */
export const HAMMERING: Hammering = {
  0: { rate: 2.6, offset: 0.2, pan: -0.45 },
  4: { rate: 2.6, offset: 0, pan: -0.2 },
  6: { rate: 2.4, offset: 0, pan: 0.2 },
  7: { rate: 2.6, offset: 0, pan: 0.35 },
  8: { rate: 2.8, offset: 0, pan: 0.15 },
};

/** Every blow of the hammer in the build, in the build scene's seconds, with where it is. */
export function hammerBlows(): { at: number; pan: number }[] {
  return blowsOf(SPOTS, HAMMERING);
}

function blow(spot: number, u: number): number {
  const hammering = HAMMERING[spot]!;
  return u * hammering.rate + hammering.offset;
}

/** The dog across the fresh concrete: when, and the line it takes in feet over the slab. */
export const PAWS = {
  cross: [2.0, 2.55] as const,
  from: { x: -1.5, z: 12.5 },
  to: { x: 13.5, z: 5.5 },
  /** The camera, in close on the trail and back out: its push in, and its pull back. */
  push: [1.85, 2.1] as const,
  pull: [2.8, 3.1] as const,
};

/** The prints the dog leaves, each where a foot came down while it was on the slab. */
function prints(): { at: number; x: number; z: number }[] {
  const out: { at: number; x: number; z: number }[] = [];
  const [start, end] = PAWS.cross;
  for (let i = 0; i <= 26; i += 1) {
    const k = i / 26;
    const x = mix(PAWS.from.x, PAWS.to.x, k);
    const z = mix(PAWS.from.z, PAWS.to.z, k) + (i % 2 === 0 ? 0.35 : -0.35);
    if (x > 0.6 && x < WORKSHOP.width - 0.6 && z > 0.6 && z < WORKSHOP.depth - 0.6) {
      out.push({ at: mix(start, end, k), x, z });
    }
  }
  return out;
}
/** The paw prints, in the order they are pressed. */
export const PRINTS = prints();

/** The last scene's moments, in its own seconds (from `AT.outside`). */
export const OUTSIDE = {
  stepBack: [0.2, 0.6] as const,
  bird: [0.35, 0.95] as const,
  song: [1.0, 1.6] as const,
  camera: [1.4, 2.0] as const,
  card: [1.55, 2.15] as const,
};

function buildScene(ink: Ink, view: View, u: number, site: Site): void {
  const outside = u - (AT.outside - AT.build);
  const done = outside >= 0;
  const card = ease.inOut(done ? span(outside, ...OUTSIDE.camera) : 0);
  // In close on the dog's trail across the wet concrete, and back out for the walls.
  const close = ease.inOut(span(u, ...PAWS.push)) - ease.inOut(span(u, ...PAWS.pull));
  const trail = on(6.5, SLAB, 8.5);
  // Outside at the end: in on the birdhouse, the maker and the lit shop, all of it inside
  // the stage, then back to take in the yard over the end card.
  const building: Camera = done
    ? { x: -20 + outside * 6, y: 80, zoom: 1.32 }
    : { x: mix(0, trail.x, close), y: mix(20, trail.y - 30, close), zoom: mix(1, 1.75, close) };
  const camera: Camera = {
    x: mix(building.x, 20, card),
    y: mix(building.y, 168, card),
    zoom: mix(building.zoom, 0.94, card),
  };
  const progress = done ? BUILT : buildProgress(u);
  const shape = WORKSHOP;

  shoot(
    ink,
    camera,
    (v) => {
      const arc = yard(ink, v, u, DAY);
      const floor = floorOf(shape);
      const height =
        floor * progress.slab +
        shape.wall * ((progress.left.raise + progress.back.raise + progress.right.raise + progress.front.raise) / 4) +
        shape.rise * progress.rafters;
      shadow(ink, SITE, shape, height, arc);

      // What is left of the lumber.
      const left = done ? 0 : Math.round(9 * (1 - clamp((u - 2) / 7)));
      for (let i = 0; i < left; i += 1) {
        ink.rect(-372 + (i % 2) * 6, GROUND - 10 - i * 9, 132, 9, { fill: ink.palette.wood, width: 1.6, offset: 0.6, overshoot: false });
      }

      const spot = cueAt(u, SPOTS);
      const popped = span(u, SPOTS[spot] ?? 0, (SPOTS[spot] ?? 0) + 0.3);
      const pressed = (): void => {
        for (const print of PRINTS) {
          if (u >= print.at) {
            const at = project(SITE, print.x, SLAB + 0.01, print.z);
            pawPrint(ink, at.x, at.y, 1.1);
          }
        }
      };
      const inside = (): void => {
        if (!done && spot === 5) {
          lift(ink, u, shape, popped);
        }
      };
      drawShed(ink, SITE, shape, progress, { floor: pressed, inside, glow: done ? span(outside, 0, 0.6) : 0 });
      if (done) {
        finale(ink, outside, u);
      } else {
        dogAtTheBuild(ink, u);
        if (spot !== 5) {
          makerAtWork(ink, u, spot, popped);
        }
      }
    },
    view,
  );

  guideCards(ink, u, STEPS, done ? 1 : 0);
  titleBlock(ink, done ? span(outside, ...OUTSIDE.card) : 0, site, {
    kind: "SHED PLANS",
    line1: "A workshop drawn",
    line2: "to your dimensions.",
  });
}

/** A point on the site in feet, on the stage. */
function on(x: number, y: number, z: number): Pt {
  return project(SITE, x, y, z);
}

function makerAtWork(ink: Ink, u: number, spot: number, popped: number): void {
  const puff = (x: number, y: number): void => dust(ink, x, y, popped < 1 ? popped : 0, 1.2);
  const front = (x: number): Pt => on(x, 0, -1.6);
  const working: Record<number, () => void> = {
    // Setting the forms: kneeling at the front corner, driving their stakes.
    0: () => {
      const at = front(-1.4);
      const phase = blow(0, u);
      drawFigure(ink, hammer(phase), { x: at.x, ground: at.y, facing: 1, hold: hammerInHand, eyes: "open", mouth: "flat" });
      impactStar(ink, at.x + 58, at.y - 10, impact(phase));
      puff(at.x, at.y);
    },
    // The pour: the barrow tipped over the forms at the front, the concrete sliding off it.
    1: () => {
      const k = span(u, ...POUR);
      const x = mix(2, 9, k);
      const wheelAt = on(x, 0, -0.7);
      const grip = wheelbarrow(ink, wheelAt.x, wheelAt.y, 0.75 + 0.15 * Math.sin(u * 3), 1 - k);
      pourStream(ink, { x: wheelAt.x + 4, y: wheelAt.y - 30 }, on(x + 1, SLAB, WORKSHOP.depth * (1 - ease.inOut(k)) + 0.3), k);
      const pose: Pose = { ...walk(u * 1.6, 0.4), lean: 0.3 };
      drawFigure(ink, pose, { x: grip.x - 40, ground: wheelAt.y, facing: 1, reachA: grip, reachB: { x: grip.x - 2, y: grip.y + 4 }, mouth: "flat" });
    },
    // Striking it off: a straight board drawn back across the wet top.
    2: () => {
      const k = span(u, POUR[1], PLAN.slab[1]);
      const z = WORKSHOP.depth * k;
      const a = on(-0.3, SLAB + 0.3, z);
      const b = on(WORKSHOP.width + 0.3, SLAB + 0.3, z);
      const feet = on(-1.6, 0, z);
      drawFigure(ink, CROUCH, { x: feet.x, ground: feet.y, facing: 1, reachA: a, reachB: { x: a.x - 6, y: a.y + 2 }, mouth: "flat" });
      screed(ink, { x: a.x - 10, y: a.y }, b);
    },
    // The sills, bolted down — and the dog, across the fresh concrete.
    3: () => {
      const at = front(3.2);
      const turning = Math.sin(u * 9) * 0.5;
      // Hands to the head as the dog trots across: there is no taking it back.
      const aghast = pulse(u, PAWS.cross[0] + 0.1, PAWS.pull[0]);
      const working: Pose = { ...KNEEL, armA: 1.2 + turning * 0.3, elbowA: 0.6 };
      const despair: Pose = { ...KNEEL, lean: -0.12, nod: -0.2, armA: 2.55, elbowA: 0.25, armB: 2.3, elbowB: 0.3 };
      const shocked = aghast > 0.3;
      drawFigure(ink, blend(working, despair, clamp(aghast * 1.6)), {
        x: at.x,
        ground: at.y,
        facing: 1,
        hold: shocked ? undefined : wrenchInHand,
        eyes: shocked ? "wide" : "open",
        mouth: shocked ? "o" : "flat",
        look: shocked ? 0.1 : 0.8,
      });
    },
    // Framing the walls flat on the slab.
    4: () => {
      const at = on(3.5, floorOf(WORKSHOP), 4);
      const phase = blow(4, u);
      drawFigure(ink, hammer(phase), { x: at.x, ground: at.y, facing: 1, hold: hammerInHand, eyes: "open", mouth: "smile" });
      impactStar(ink, at.x + 58, at.y - 8, impact(phase));
      puff(at.x, at.y);
    },
    // Up the ladder for the caps and the rafters.
    6: () => {
      const foot = on(WORKSHOP.width + 1.4, 0, -0.6);
      const top = on(WORKSHOP.width - 0.4, floorOf(WORKSHOP) + WORKSHOP.wall + 0.6, -0.4);
      ladder(ink, foot, top);
      const phase = blow(6, u);
      drawFigure(ink, hammerStanding(phase), { x: top.x + 26, ground: top.y + 92, facing: -1, hold: hammerInHand, eyes: "open", mouth: "flat" });
      impactStar(ink, top.x - 30, top.y - 30, impact(phase));
    },
    // The siding, down the window wall.
    7: () => {
      const at = on(WORKSHOP.width + 1.6, 0, 3 + clamp((u - 6.95) / 1) * 9);
      const phase = blow(7, u);
      drawFigure(ink, hammerStanding(phase), { x: at.x, ground: at.y, facing: -1, hold: hammerInHand, eyes: "open", mouth: "smile" });
      impactStar(ink, at.x - 52, at.y - 100, impact(phase));
      puff(at.x, at.y);
    },
    // On the roof.
    8: () => {
      const along = 3 + clamp((u - 8.0) / 0.9) * 8;
      const at = on(8.6, roofHeight(WORKSHOP, 8.6) + 0.3, along);
      const phase = blow(8, u);
      drawFigure(ink, hammer(phase), { x: at.x, ground: at.y, facing: 1, hold: hammerInHand, eyes: "open", mouth: "flat" });
      impactStar(ink, at.x + 58, at.y - 26, impact(phase), 0.8);
    },
    // Hanging the door.
    9: () => {
      const at = front(WORKSHOP.width / 2 + 2.6);
      drawFigure(ink, { ...STAND, armA: 1.3, elbowA: 0.5, armB: 1.2, elbowB: 0.6 }, { x: at.x, ground: at.y, facing: -1, eyes: "open", mouth: "smile" });
    },
  };
  working[spot]?.();
}

/** Standing the front wall: up from a crouch at its top plate, walking it upright. */
function lift(ink: Ink, u: number, shape: Shape, popped: number): void {
  const raise = span(u, PLAN.front[1], PLAN.front[2]);
  const angle = (Math.PI / 2) * ease.out(raise);
  const z = shape.wall * Math.cos(angle) + 0.9;
  const feet = on(shape.width * 0.42, floorOf(shape), z);
  const hands = frontTop(SITE, shape, raise);
  const pose = blend(CROUCH, { ...STAND, lean: 0.08, armA: 2.4, armB: 2.3, elbowA: 0.3, elbowB: 0.3 }, ease.inOut(span(raise, 0, 0.7)));
  drawFigure(ink, pose, {
    x: feet.x + 8,
    ground: feet.y,
    facing: -1,
    reachA: hands,
    reachB: { x: hands.x - 14, y: hands.y + 2 },
    eyes: raise > 0.2 && raise < 0.9 ? "closed" : "open",
    mouth: raise > 0.2 && raise < 0.9 ? "grin" : "o",
  });
  dust(ink, feet.x, feet.y, popped < 1 ? popped : 0, 1);
  if (raise >= 1) {
    sparkle(ink, hands.x, hands.y - 20, span(u, PLAN.front[2], PLAN.front[2] + 0.3));
  }
}

/** The dog: by the tree, then across the wet slab at a trot, then asleep by the lumber. */
function dogAtTheBuild(ink: Ink, u: number): void {
  const cross = span(u, ...PAWS.cross);
  if (cross > 0 && cross < 1) {
    const x = mix(PAWS.from.x, PAWS.to.x, cross);
    const z = mix(PAWS.from.z, PAWS.to.z, cross);
    const onSlab = x > 0 && x < WORKSHOP.width && z > 0 && z < WORKSHOP.depth;
    const at = on(x, onSlab ? SLAB : 0, z);
    drawDog(ink, { x: at.x, ground: at.y, facing: 1, mode: "walk", phase: u * 7, t: u });
    return;
  }
  if (cross >= 1) {
    const at = on(WORKSHOP.width + 2.5, 0, -1.2);
    drawDog(ink, { x: at.x, ground: at.y, facing: -1, mode: u > 5 ? "sleep" : "sit", t: u });
    return;
  }
  drawDog(ink, { x: -300, ground: GROUND + 4, facing: 1, mode: "sit", t: u });
}

/** Done: the birdhouse hung in the tree, the maker stepped back from it, a bird moving in. */
function finale(ink: Ink, outside: number, u: number): void {
  const branch = { x: -318, y: 104 + Math.sin(u * 1.4) * 2 };
  const hang = { x: branch.x, y: branch.y + 74 };
  ink.line(branch.x, branch.y, hang.x, hang.y - 52, { width: 1.4, overshoot: false });
  birdhouse(ink, hang.x, hang.y, 1, Math.sin(outside * 3) * 0.04 * (1 - clamp(outside)));

  const back = ease.inOut(span(outside, ...OUTSIDE.stepBack));
  const reaching: Pose = { ...STAND, armA: 2.6, elbowA: 0.3, armB: 0.3, elbowB: 0.3 };
  const pose = blend(reaching, blend(idle(outside), THUMBS_UP, clamp(back * 1.5 - 0.5)), back);
  drawFigure(ink, pose, {
    x: mix(-268, -220, back),
    ground: GROUND,
    facing: -1,
    reachA: back < 0.3 ? { x: hang.x + 10, y: hang.y - 30 } : undefined,
    eyes: "happy",
    mouth: "grin",
    thumbA: back > 0.7,
    look: 0.3,
  });
  drawDog(ink, { x: -150, ground: GROUND + 4, facing: -1, mode: "sit", t: u });

  const fly = ease.out(span(outside, ...OUTSIDE.bird));
  const perch = { x: hang.x + 2, y: hang.y - 58 };
  bird(ink, mix(perch.x + 420, perch.x, fly), mix(perch.y - 240, perch.y, fly), outside * 30, fly >= 1);
  note(ink, perch.x + 18, perch.y - 18, span(outside, ...OUTSIDE.song));
}

// ---- 6. inside ------------------------------------------------------------------------------

/** The inside scene's moments, in its own seconds. */
export const INSIDE = {
  sanding: [0.1, 0.85] as const,
  /** Sanding strokes a second. */
  rate: 3.4,
  lift: [0.85, 1.15] as const,
  blow: [1.1, 1.4] as const,
  shine: [1.2, 1.55] as const,
};

function insideScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  const floor = GROUND;
  const camera: Camera = { x: mix(30, 40, u / 1.6), y: 70, zoom: mix(1.62, 1.7, u / 1.6) };
  shoot(
    ink,
    camera,
    (v) => {
      // The back wall, studs showing, and the concrete floor.
      ink.wash(
        [
          { x: v.left - 20, y: v.top - 20 },
          { x: v.right + 20, y: v.top - 20 },
          { x: v.right + 20, y: floor },
          { x: v.left - 20, y: floor },
        ],
        p.wood,
        0.32,
        0,
      );
      for (let x = Math.floor(v.left / 64) * 64; x < v.right + 64; x += 64) {
        ink.line(x, v.top - 20, x, floor, { width: 1.4, alpha: 0.22, overshoot: false });
      }
      ink.wash(
        [
          { x: v.left - 20, y: floor },
          { x: v.right + 20, y: floor },
          { x: v.right + 20, y: v.bottom + 20 },
          { x: v.left - 20, y: v.bottom + 20 },
        ],
        p.roof,
        0.22,
        0,
      );
      ink.line(v.left - 20, floor, v.right + 20, floor, { width: 2.6 });

      const glow = 0.8 + 0.2 * Math.sin(u * 2);
      shopWindow(ink, 20, -150, 96, 100, floor, glow);
      shopWindow(ink, 160, -150, 96, 100, floor, glow);
      pegboard(ink, -232, -176, 220, 130);
      workbench(ink, -100, 290, 168, floor);

      // The prints the dog left in the slab, by its mat.
      for (const [i, x] of [92, 124, 156, 188].entries()) {
        pawPrint(ink, x, floor + 26 + (i % 2) * 10, 1.6);
      }
      mat(ink, 250, floor + 44);
      drawDog(ink, { x: 250, ground: floor + 44, facing: -1, mode: "sleep", t: u });

      // The maker, sanding, then holding it up to the light.
      const sand = stroke(u, ...INSIDE.sanding, INSIDE.rate);
      const lifted = ease.out(span(u, ...INSIDE.lift));
      const house = { x: mix(-78, -120, lifted), y: mix(168, 96, lifted) };
      const angle = Math.sin(u * 2.4) * 0.05 * lifted;
      birdhouse(ink, house.x, house.y, 1.3, angle);
      const pose: Pose = blend({ ...STAND, lean: 0.2 }, { ...STAND, lean: -0.05, nod: -0.15 }, lifted);
      drawFigure(ink, pose, {
        x: -168,
        ground: floor,
        facing: 1,
        reachA: lifted > 0 ? { x: house.x - 18, y: house.y - 24 } : { x: house.x - 26, y: house.y - 30 + sand * 12 },
        reachB: lifted > 0 ? { x: house.x + 18, y: house.y - 16 } : { x: house.x - 22, y: house.y - 8 },
        eyes: lifted > 0.5 ? "happy" : "open",
        mouth: lifted > 0.5 ? "grin" : "smile",
        look: 0.6,
      });
      dust(ink, house.x + 6, house.y - 40, span(u, ...INSIDE.blow), 0.7);
      sparkle(ink, house.x + 26, house.y - 70, span(u, ...INSIDE.shine), 14);
    },
    view,
  );
}
